import { Component, Input, OnDestroy, OnInit, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { Router } from '@angular/router';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { Address, Building, NewBuilding, NewRoom, NewUnit, Organization, PurchaseProtectionAct, Renovation, Room, Unit, Valuation } from '@app/core/stingray-api/models';
import { BuildingInfoService, BuildingsService, MortgagesService } from '@app/core/stingray-api/services';
import { ConfirmDialogComponent, ConfirmDialogData } from '@app/shared/confirm-dialog/confirm-dialog.component';
import { Shell } from '@app/shell/shell.service';
import { conditionalValidator } from '@app/validators/conditional-validator';
import { getFormValidationErrors, updateTreeValidity } from '@app/validators/form-validation-tools';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import _, { add } from 'lodash';
import moment from 'moment';
import { concat, from, Observable, of, Subscription } from 'rxjs';
import { concatMap, finalize, last, map } from 'rxjs/operators';
import { toDateOnly } from '@app/shared/utils/date-utils';

import { EditBuildingPanelModel } from './edit-building-panel-model';
import { EditBuildingPanelOptions } from './edit-building-panel-options';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { ListItemReadComponent } from '../list/list-item-read/list-item-read.component';
import { ListItemInputComponent } from '../list/list-item-input/list-item-input.component';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatFormField } from '@angular/material/form-field';
import { MatList, MatListItem } from '@angular/material/list';
import { InputFieldComponent } from '../forms/input-field/input-field.component';
import { ListItemSelectComponent } from '../list/list-item-select/list-item-select.component';
import { MatSort } from '@angular/material/sort';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgClass, NgTemplateOutlet, CurrencyPipe, DatePipe } from '@angular/common';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelActionRow } from '@angular/material/expansion';

class UnitRow {
  address: Address;
  form: UntypedFormGroup;
  unit: Unit;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
@Component({
    selector: 'unite-edit-building-panel',
    templateUrl: './edit-building-panel.component.html',
    styleUrls: ['./edit-building-panel.component.scss'],
    imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, NgClass, MatProgressSpinner, MatIconButton, MatTooltip, MatIcon, TranslateModule, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, ListItemSelectComponent, InputFieldComponent, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatList, MatListItem, MatFormField, MatSelect, ReactiveFormsModule, MatOption, NgTemplateOutlet, ListItemInputComponent, ListItemReadComponent, MatExpansionPanelActionRow, LoadingButtonComponent, CurrencyPipe, DatePipe]
})
export class EditBuildingPanelComponent implements OnInit, OnDestroy {

  @Input() processBuilding: Observable<void>;

  @Input() model: EditBuildingPanelModel;
  @Input() options: Partial<EditBuildingPanelOptions>;

  buildingIgnored = output<EditBuildingPanelModel>();
  buildingProcessed = output<EditBuildingPanelModel>();
  buildingRemoved = output<EditBuildingPanelModel>();
  buildingSold = output<EditBuildingPanelModel>();

  statusPrefilled = false;

  units: UnitRow[] = [];
  rooms: Room[] = [];

  purchaseProtectionAct: PurchaseProtectionAct;

  isLoadingBuildingInfo = false;

  organization: Organization;
  form: UntypedFormGroup;

  limitedByPointsRent = false;

  confirmedDeletingOpenRenovations: boolean;
  recentValuation: Valuation;

  unitsDataSource: MatTableDataSource<UnitRow> = new MatTableDataSource();
  unitsDisplayedColumns: string[] = ['label', 'type'];

  private processBuildingSubscription: Subscription;
  private defaultOptions: EditBuildingPanelOptions = {
    allowEmptyFields: false,
    linkToMortgage: false,
    showBuildingLink: false,
    showCustomerName: false,
    showEnergy: true,
    showEstimatedValueByMunicipality: true,
    showGroundLease: false,
    showIgnoreButton: false,
    showOwners: false,
    showPurchaseDate: false,
    showPurchaseLabel: true,
    showPurchaseValue: false,
    showRecentValuation: true,
    showRemoveButton: false,
    showRenovations: true,
    showRentalPoints: true,
    showRentIncome: true,
    showSoldButton: false,
    showRentalType: true,
    showSurface: true,
    showValueInRentedCondition: true,
    skipPointsRentWarning: false,
  };

  constructor(
    private authenticationService: StingrayAuthenticationService,
    private buildingInfoService: BuildingInfoService,
    private buildingsService: BuildingsService,
    private formBuilder: FormBuilder,
    private matDialog: MatDialog,
    private mortgagesService: MortgagesService,
    private router: Router,
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.organization = this.authenticationService.organization;

    this.loadModel();

    if (this.options.showEnergy === true) {
      this.unitsDisplayedColumns.push('finalEnergyLabel');
    }

    this.unitsDisplayedColumns.push('rented');

    if (this.options.showRentIncome === true) {
      this.unitsDisplayedColumns.push('rentIncome');
    }

    if (this.options.showRentalType === true) {
      this.unitsDisplayedColumns.push('rentalType');
    }

    if (this.options.showRentalPoints === true) {
      this.unitsDisplayedColumns.push('rentalPoints');
    }

    // if (this.options.showSurface === true) {
    //   this.unitsDisplayedColumns.push('surface');
    // }

    // if (this.options.showEstimatedValueByMunicipality === true) {
    //   this.unitsDisplayedColumns.push('estimatedValueByMunicipality');
    //   this.unitsDisplayedColumns.push('estimatedValueByMunicipalityDate');
    // }
  }

  ngOnDestroy(): void {
    if (this.processBuildingSubscription) {
      this.processBuildingSubscription.unsubscribe();
    }
  }

  showBuildingLink(): boolean {
    return this.options.showBuildingLink === true
      && this.organization.subscription === 'Professional'
      && this.model.building?.id !== undefined;
  }

  openBuilding(): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/customers', this.model.customerId, 'buildings', this.model.building.id, 'detail']));
    window.open(url, '_blank');
  }

  openPanel(){
    this.model.isProcessed = false;
  }

  openRenovations(): Renovation[] {

    if (!this.model.building || !this.model.building.renovations) {
      return [];
    }

    const openRenovations = this.model.building.renovations.filter(x => !x.completed);
    return openRenovations;
  }

  ignore(): void {
    this.buildingIgnored.emit(this.model);
  }

  sold(): void {
    this.buildingSold.emit(this.model);
  }

  remove(): void {
    this.buildingRemoved.emit(this.model);
  }

  highestValueInRentedConditionAfterRenovation(): number {

    if (this.openRenovations().length === 0) {
      return null;
    }

    return _.orderBy(this.openRenovations(), x => x.valueInRentedConditionAfterRenovation, 'desc')[0].valueInRentedConditionAfterRenovation;
  }

  bestEnergyLabelAfterRenovation(): string {
    if (this.openRenovations().length === 0) {
      return null;
    }

    return _.orderBy(this.openRenovations(), x => x.energyLabelAfterRenovation, 'asc')[0].energyLabelAfterRenovation;
  }

  openRenovationCosts(): number {
    return this.openRenovations().reduce((sum, current) => sum + current.costs, 0);
  }

  saveBuildingClick(): void {
    this.saveBuilding().subscribe();
  }

  saveBuilding(): Observable<void> {
    return new Observable(obs => {
      updateTreeValidity(this.form);
      getFormValidationErrors(this.form).forEach(e => console.log(this.model.name, e, this.form.value, this.options, this.model));

      this.form.markAllAsTouched();

      if (this.form.valid) {
        this.model.isProcessing = true;
        this.doSaveBuilding(false).subscribe(() => {
          obs.next();
          obs.complete();
        });
      } else {
        obs.next();
        obs.complete();
      }
    });
  }

  saveBuildingDirty(): Observable<void> {
    return new Observable(obs => {
      this.model.isProcessing = true;
      this.options.allowEmptyFields = true;
      this.options.skipPointsRentWarning = true;
      this.doSaveBuilding(true).subscribe(() => {
        obs.next();
        obs.complete();
      });
    });
  }

  allowEmptyFields(): boolean {
    return this.options.allowEmptyFields;
  }

  getRentFilled(form: FormGroup): number {
    return form.get('rented').value ? form.get('currentRentIncome').value : form.get('estimatedRentalValue').value;
  }

  hasCommercialUnits(): boolean{
    return this.units.filter(x => !this.isResidential(x.form)).length > 0;
  }

  hasResidentialUnits(): boolean{
    return this.units.filter(x => this.isResidential(x.form)).length > 0;
  }

  isCombinationBuilding(form: FormGroup): boolean {
    return form.get('type').value === 'Combination';
  }

  isForRent(form: FormGroup): boolean {
    return form.get('purpose').value === 'Rent';
  }

  isForSell(form: FormGroup): boolean {
    return form.get('purpose').value === 'Sell';
  }

  isPointsRent(form: FormGroup): boolean {
    return this.isResidential(form) &&
      form.get('rentalType').value !== 'RoomRental' &&
      form.get('rentalType').value !== 'ShortStay';
  }

  isResidential(form: FormGroup): boolean {
    const RESI_TYPES = ['Apartment', 'House', 'Studio'];
    return RESI_TYPES.indexOf(form.get('type').value) > -1;
  }

  isFinanced(form: FormGroup): boolean {
    return form.get('financed').value;
  }

  isPurchase(form: FormGroup) {
    return form.get('status').value === 'Purchase';
  }

  isRented(form: FormGroup) {
    return form.get('rented').value === true;
  }

  isRentFilled(form: FormGroup): boolean {
    return form.get('rented').value ? !!form.get('currentRentIncome').value : !!form.get('estimatedRentalValue').value;
  }

  isValueInRentedConditionFilled(form: FormGroup): boolean {
    return !!form.get('valueInRentedCondition').value;
  }

  showCurrentRentIncome(form: FormGroup): boolean {
    return !this.isCombinationBuilding(form) && this.isRented(form) && this.options.showRentIncome === true;
  }

  showEstimatedRentalValue(form: FormGroup): boolean {
    return !this.isCombinationBuilding(form) && !this.isRented(form) && this.options.showRentIncome === true;
  }

  showValueInRentedCondition(form: FormGroup): boolean {
    return this.options.showValueInRentedCondition === true && this.isForRent(form);
  }

  showExpectedValue(form: FormGroup): boolean {
    return this.isForSell(form);
  }

  showRentalType(form: FormGroup): boolean {
    return this.options.showRentalType === true && this.isResidential(form)
  }

  showRentedTo(form: FormGroup): boolean {
    return this.options.showRentalType === true && this.isResidential(form)
  }

  showSublet(form: FormGroup): boolean {
    return this.options.showRentalType === true && this.isResidential(form)
  }

  showEstimatedValueByMunicipality(form: FormGroup): boolean {
    return !this.isCombinationBuilding(form) && this.options.showEstimatedValueByMunicipality;
  }

  showGroundLease(): boolean {
    return this.options.showGroundLease;
  }

  showIsRented(form: FormGroup){
    return !this.isCombinationBuilding(form) && this.options.showRentIncome === true;
  }

  showRenovations(): boolean {
    return this.options.showRenovations;
  }

  showRentalPoints(form: FormGroup): boolean {
    return this.isPointsRent(form) && this.options.showRentalPoints;
  }

  showCalculatedRentalPoints(form: FormGroup): boolean {
    return this.isPointsRent(form) && this.options.showRentalPoints && form.get('pointsRent').get('totalPoints').value !== null;
  }

  showEnergy(form: FormGroup): boolean {
    return !this.isCombinationBuilding(form) && this.options.showEnergy;
  }

  showSurface(form: FormGroup): boolean {
    return !this.isCombinationBuilding(form) && this.options.showSurface;
  }

  showNumberOfRentalContracts(form: FormGroup): boolean {
    return this.options.showRentalType === true && this.isRoomRental(form);
  }

  showNumberOfSubletContracts(form: FormGroup): boolean {
    return this.options.showRentalType === true && this.isSublet(form);
  }

  showPurchaseProtectionAct(form: FormGroup): boolean {
    return this.limitedByPointsRent == false &&
      this.purchaseProtectionAct &&
      this.isPurchase(form) &&
      ['House', 'Apartment'].indexOf(form.get('type').value) > -1 &&
      (!form.get('estimatedValueByMunicipality').value || form.get('estimatedValueByMunicipality').value < this.purchaseProtectionAct.maxEstimatedValueByMunicipality)
  }

  showPurchaseLabel(form: FormGroup): boolean {
    return this.options.showPurchaseLabel === true && this.isPurchase(form);
  }

  showRecentValuation(form: FormGroup): boolean {
    return this.options.showRecentValuation && !_.isUndefined(this.recentValuation) && !form.get('valueInRentedCondition').touched;
  }

  showPurchaseDate(): boolean {
    return this.options.showPurchaseDate === true;
  }

  showPurchaseValue(): boolean {
    return this.options.showPurchaseValue === true;
  }

  showCustomerName() {
    return this.options.showCustomerName;
  }

  numberOfRentalContracts(form: FormGroup): number {
    return this.isRoomRental(form) ? form.get('numberOfRentalContracts').value : 1;
  }

  willBeRenovated(form: FormGroup): boolean {
    return this.options.showRenovations === true && form.get('willBeRenovated').value;
  }

  private isRoomRental(form: FormGroup): boolean {
    return this.isResidential(form) ? form.get('rentalType').value === 'RoomRental' : null;
  }

  private isSublet(form: FormGroup): boolean {
    return this.isResidential(form) ? form.get('sublet').value : null;
  }

  private numberOfSubletContracts(form: FormGroup): number {
    return form.get('sublet').value ? form.get('numberOfSubletContracts').value : 0;
  }

  private loadModel(): void {
    // Use default options if no options are supplied otherwise merge them
    if (!this.options) {
      this.options = this.defaultOptions;
    } else {
      this.options = { ...this.defaultOptions, ...this.options };
    }

    this.form = this.createBuildingFormGroup();

    if (this.model.building) {
      this.loadExistingBuilding(this.model.building);
    }

    this.statusPrefilled = !this.model.building?.status == null;

    if (!this.model.additionalAddresses) {
      this.model.additionalAddresses = [];
    }

    if (this.model.additionalAddresses.length > 0 && this.isNewBuilding()) {
      this.form.get('type').setValue('Combination');

      this.model.additionalAddresses.forEach(a => {

        const unitRow = {
          address: a,
          form: this.createUnitFormGroup()
        } as UnitRow;

        this.units.push(unitRow);

        this.getAddressInfo(unitRow.address, unitRow.form, true);

      });

      this.unitsDataSource.data = this.units;
    }

    // Attach observer to trigger save action outside of scope
    if (this.processBuilding) {
      this.processBuildingSubscription = this.processBuilding.subscribe(() => this.saveBuilding().subscribe());
    }
  }

  private getAddressInfo(address: Address, form: FormGroup, overrideType: boolean){
     this.isLoadingBuildingInfo = true;

    this.buildingInfoService.getBuildingInfo({
      postalCode: address.postalCode,
      houseNumber: parseInt(address.houseNumber, 10),
      houseNumberAddition: address.houseNumberAddition,
      includeRegistration: true,
      includeValue: this.options.showEstimatedValueByMunicipality,
      includeEnergy: this.options.showEnergy,
      includePurchaseProtectionAct: true
    })
      .pipe(finalize(() => this.isLoadingBuildingInfo = false))
      .subscribe(info => {
        this.purchaseProtectionAct = info.purchaseProtectionAct;

        form.get('estimatedValueByMunicipality').setValue(info.value?.value, { emitEvent: false });
        form.get('estimatedValueByMunicipalityDate').setValue(moment(info.value?.startDate), { emitEvent: false });
        form.get('finalEnergyLabel').setValue(info.energy?.energyLabel, { emitEvent: false });
        form.get('energyIndex').setValue(null, { emitEvent: false });
        form.get('energyPerformanceRegistrationDate').setValue(info.energy?.registrationDate, { emitEvent: false });
        form.get('energyPerformanceExpiryDate').setValue(info.energy?.validUntil, { emitEvent: false });   
        form.get('surface').setValue(info.registration?.surface, { emitEvent: false });

        if (overrideType){
          form.get('type').setValue(info.registration?.type, { emitEvent: false });
        }
        
        this.updatePointsRent(form);
      });

  }


  private subscribePointsRent(form: FormGroup): void {
    form.get('finalEnergyLabel').valueChanges.subscribe(() => this.updatePointsRent(form));
    form.get('estimatedValueByMunicipality').valueChanges.subscribe(() => this.updatePointsRent(form));
    form.get('surface').valueChanges.subscribe(() => this.updatePointsRent(form));
    form.get('estimatedRentalValue').valueChanges.subscribe(() => this.updatePointsRent(form));
  }

  /**
   * Creates a form group for a unit
   */
  private createUnitFormGroup(): UntypedFormGroup {
    const fg = this.formBuilder.group({
      constructionYear: [],
      currentRentIncome: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      energyIndex: [],
      energyPerformanceRegistrationDate: [],
      energyPerformanceExpiryDate: [],
      estimatedValueByMunicipality: [],
      estimatedValueByMunicipalityDate: [],
      estimatedRentalValue: [],
      finalEnergyLabel: [],
      numberOfRentalContracts: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      numberOfSubletContracts: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      pointsRent: this.formBuilder.group({
        totalPoints: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
        manualPoints: [, []],
        rent: []
      }),      
      rentalType: ['Regular', [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      rented: [true, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      rentedTo: ['Family', [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      sublet: [, []],
      surface: [],
      type: [, [Validators.required]]
    });

    fg.valueChanges.subscribe(() => {
      this.unitFormGroupChecker(fg);
      this.buildingFormGroupChecker(this.form);
    });

    this.subscribePointsRent(fg);
    this.unitFormGroupChecker(fg);

    return fg;
  }

  /**
   * Creates a form group for a building
   */
  private createBuildingFormGroup(): UntypedFormGroup {

    const fg = this.formBuilder.group({
      constructionYear: [],
      currentRentIncome: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      divideValueInRentedConditionOverUnits: [true, ],
      energyIndex: [],
      energyPerformanceRegistrationDate: [],
      energyPerformanceExpiryDate: [],
      estimatedRentalValue: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      estimatedValueByMunicipality: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      estimatedValueByMunicipalityDate: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      expectedValue: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      finalEnergyLabel: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      groundLease: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      numberOfRentalContracts: [1, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      numberOfSubletContracts: [1, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],      
      pointsRent: this.formBuilder.group({
        totalPoints: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
        manualPoints: [, []],
        rent: []
      }),
      purchaseDate: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      purchaseValue: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      purpose: ['Rent', [Validators.required]],
      renovationCosts: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      energyLabelAfterRenovation: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      renovationPermitsRequired: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      rentalType: ['Regular', [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      rented: [true, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      rentedTo: ['Family', [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      status: [, [Validators.required]],
      sublet: [false, []],
      surface: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      type: [, [Validators.required]],
      valueInRentedCondition: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      valueInRentedConditionCommercial: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      valueInRentedConditionResidential: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      valueInRentedConditionAfterRenovation: [, [conditionalValidator(() => this.allowEmptyFields() == false, Validators.required)]],
      willBeRenovated: [false, [Validators.required]],
    }, { updateOn: 'blur' });

    fg.controls.willBeRenovated.valueChanges.subscribe(() => {
      // fg.controls.valueInRentedConditionAfterRenovation.updateValueAndValidity();
      // fg.controls.renovationCosts.updateValueAndValidity({ emitEvent: false });
      // fg.controls.renovationPermitsRequired.updateValueAndValidity({ emitEvent: false });

      // Check if this action conflicts with earlier set data
      this.showDeletedRenovationWarning();
    });

    fg.valueChanges.subscribe(() => {
      this.buildingFormGroupChecker(fg);
    });

    this.subscribePointsRent(fg);
    this.buildingFormGroupChecker(fg);

    // fg.controls.type.valueChanges.subscribe(() => {
    //   fg.controls.roomRental.updateValueAndValidity();
    // });

    // fg.controls.roomRental.valueChanges.subscribe(() => {
    //   fg.controls.numberOfRentalContracts.updateValueAndValidity();
    // });

    // fg.controls.sublet.valueChanges.subscribe(() => {
    //   fg.controls.numberOfSubletContracts.updateValueAndValidity();
    // });

    fg.controls.renovationCosts.valueChanges.subscribe(() => {
      // Check if this action conflicts with earlier set data
      this.showDeletedRenovationWarning();
    });

    // fg.controls.purpose.valueChanges.subscribe(() => {
    //   fg.controls.expectedValue.updateValueAndValidity({ emitEvent: false });
    //   fg.controls.valueInRentedCondition.updateValueAndValidity({ emitEvent: false });
    // });

    return fg;
  }

  private buildingFormGroupChecker(form: FormGroup) {
    this.unitFormGroupChecker(form);
    
    if (!this.isCombinationBuilding(form)) {
      form.get('type').enable({ emitEvent: false });
    } else {
      form.get('type').disable({ emitEvent: false });
    }

    if (this.showExpectedValue(form)) {
      form.get('expectedValue').enable({ emitEvent: false });
    } else {
      form.get('expectedValue').disable({ emitEvent: false });
    }

    if (this.showGroundLease()) {
      form.get('groundLease').enable({ emitEvent: false });
    } else {
      form.get('groundLease').disable({ emitEvent: false });
    }

    if (this.showPurchaseDate()) {
      form.get('purchaseDate').enable({ emitEvent: false });
    } else {
      form.get('purchaseDate').disable({ emitEvent: false });
    }

    if (this.showPurchaseValue()) {
      form.get('purchaseValue').enable({ emitEvent: false });
    } else {
      form.get('purchaseValue').disable({ emitEvent: false });
    }

    if (this.showRenovations()) {
      form.get('willBeRenovated').enable({ emitEvent: false });
    } else {
      form.get('willBeRenovated').disable({ emitEvent: false });
    }
       
    if (this.showValueInRentedCondition(form)) {
      if (form.get('type').value === 'Combination' && form.get('divideValueInRentedConditionOverUnits').value) {
        form.get('valueInRentedCondition').disable({ emitEvent: false });

        if (this.hasCommercialUnits()){
          form.get('valueInRentedConditionCommercial').enable({ emitEvent: false });
        } 
        else{
          form.get('valueInRentedConditionCommercial').disable({ emitEvent: false });
        }
        
        if (this.hasResidentialUnits()){
          form.get('valueInRentedConditionResidential').enable({ emitEvent: false });
        } 
        else{
          form.get('valueInRentedConditionResidential').disable({ emitEvent: false });
        }
      }
      else{
        form.get('valueInRentedCondition').enable({ emitEvent: false });
        form.get('valueInRentedConditionCommercial').disable({ emitEvent: false });
        form.get('valueInRentedConditionResidential').disable({ emitEvent: false });
      }
    } else {
      form.get('valueInRentedCondition').disable({ emitEvent: false });
      form.get('valueInRentedConditionCommercial').disable({ emitEvent: false });
      form.get('valueInRentedConditionResidential').disable({ emitEvent: false });
    }

    if (this.willBeRenovated(form)) {
      form.get('renovationCosts').enable({ emitEvent: false });
      form.get('renovationPermitsRequired').enable({ emitEvent: false });
      form.get('valueInRentedConditionAfterRenovation').enable({ emitEvent: false });
      
      if (this.showEnergy(form)) {
        form.get('energyLabelAfterRenovation').enable({ emitEvent: false });
      } else {
        form.get('energyLabelAfterRenovation').disable({ emitEvent: false });
      }
    } else {
      form.get('renovationCosts').disable({ emitEvent: false });
      form.get('energyLabelAfterRenovation').disable({ emitEvent: false });
      form.get('renovationPermitsRequired').disable({ emitEvent: false });
      form.get('valueInRentedConditionAfterRenovation').disable({ emitEvent: false });
    }
  }

  private unitFormGroupChecker(form: FormGroup) {
    
    if (this.showCalculatedRentalPoints(form)){
      form.get('pointsRent').get('totalPoints').enable({ emitEvent: false });
    } else {
      form.get('pointsRent').get('totalPoints').disable({ emitEvent: false });
    }

    if (this.showCurrentRentIncome(form)) {
      form.get('currentRentIncome').enable({ emitEvent: false });
    } else {
      form.get('currentRentIncome').disable({ emitEvent: false });
    }

    if (this.showEnergy(form)) {
      form.get('finalEnergyLabel').enable({ emitEvent: false });
    } else {
      form.get('finalEnergyLabel').disable({ emitEvent: false });
    }

    if (this.showEstimatedRentalValue(form)) {
      form.get('estimatedRentalValue').enable({ emitEvent: false });
    } else {
      form.get('estimatedRentalValue').disable({ emitEvent: false });
    }    
    
    if (this.showEstimatedValueByMunicipality(form)) {
      form.get('estimatedValueByMunicipality').enable({ emitEvent: false });
      form.get('estimatedValueByMunicipalityDate').enable({ emitEvent: false });
    } else {
      form.get('estimatedValueByMunicipality').disable({ emitEvent: false });
      form.get('estimatedValueByMunicipalityDate').disable({ emitEvent: false });
    }

    if (this.showIsRented(form)) {
      form.get('rented').enable({ emitEvent: false });
    } else {
      form.get('rented').disable({ emitEvent: false });
    }

    if (this.showRentedTo(form)) {
      form.get('rentedTo').enable({ emitEvent: false });
    } else {
      form.get('rentedTo').disable({ emitEvent: false });
    }

    if (this.showNumberOfRentalContracts(form)) {
      form.get('numberOfRentalContracts').enable({ emitEvent: false });
    } else {
      form.get('numberOfRentalContracts').disable({ emitEvent: false });
    }
             
    if (this.showNumberOfSubletContracts(form)) {
      form.get('numberOfSubletContracts').enable({ emitEvent: false });
    } else {
      form.get('numberOfSubletContracts').disable({ emitEvent: false });
    }

    if (this.showRentalPoints(form)) {
      form.get('pointsRent').get('manualPoints').enable({ emitEvent: false });
    } else {
      form.get('pointsRent').get('manualPoints').disable({ emitEvent: false });
    }

    if (this.showRentalType(form)) {
      form.get('rentalType').enable({ emitEvent: false });
    } else {
      form.get('rentalType').disable({ emitEvent: false });
    }

    if (this.showSurface(form)) {
      form.get('surface').enable({ emitEvent: false });
    } else {
      form.get('surface').disable({ emitEvent: false });
    }

    if (this.showSublet(form)) {
      form.get('sublet').enable({ emitEvent: false });
    } else {
      form.get('sublet').disable({ emitEvent: false });
    }
  }

  updatePointsRent(form: FormGroup): void {
    this.buildingInfoService
      .calculatePointsRent({
        buildingType: form.get('type').value,
        constructionYear: form.get('constructionYear').value,   
        energyIndex: form.get('energyIndex').value,
        energyLabel: form.get('finalEnergyLabel').value,
        energyPerformanceRegistrationDate: toDateOnly(form.get('energyPerformanceRegistrationDate')?.value),
        estimatedValueByMunicipality: form.get('estimatedValueByMunicipality').value,
        estimatedValueByMunicipalityDate: toDateOnly(form.get('estimatedValueByMunicipalityDate')?.value),
        roomRental: this.isRoomRental(form),
        surface: form.get('surface').value
      })
      .subscribe(pointsRent => {
        form.get('pointsRent').get('totalPoints').setValue(pointsRent.totalPoints);
      });
  }

  /* Patches the building into the existing address row */
  private loadExistingBuilding(existingBuilding: Building): void {
    this.model.building = existingBuilding;

    if (existingBuilding.address){
      this.model.name = `${existingBuilding.address.addressLine1}, ${existingBuilding.address.city}`;
    }

    if (existingBuilding.rentalType == 'RoomRental') {
      this.buildingsService.getRooms({ id: existingBuilding.id }).subscribe(rooms => {
        this.rooms = rooms;
        //this.form.get('estimatedRentalValue').setValue(rooms.reduce((sum, current) => sum + current.estimatedRentalValue, 0));
        //this.form.get('valueInRentedCondition').setValue(existingBuilding.selectedValue != null ? existingBuilding.selectedValue.value : existingBuilding.valueInRentedCondition);

      });
    } else if (existingBuilding.type === 'Combination') {
      this.buildingsService.getUnits({ id: existingBuilding.id }).subscribe(units => {
        this.units = units.map(u => {

          const row = {
            address: u.address,
            unit: u,
            form: this.createUnitFormGroup()
          } as UnitRow;

          row.form.patchValue(u);
          
          row.form.get('type').setValue(u.type);
          return row;
        });

        this.unitsDataSource.data = this.units;

        // const totalValueInRentedCondition = this.units.reduce((sum, current) => sum + current.valueInRentedCondition, 0);
        // this.form.get('valueInRentedCondition').setValue(totalValueInRentedCondition);
      });
    } else {
      // this.form.get('valueInRentedCondition').setValue(existingBuilding.selectedValue != null ? existingBuilding.selectedValue.value : existingBuilding.valueInRentedCondition);
      // this.form.get('estimatedRentalValue').setValue(existingBuilding.estimatedRentalValue);
    }

    this.form.patchValue(existingBuilding, { emitEvent: false });
    this.form.get('valueInRentedCondition').setValue(existingBuilding.selectedValue != null ? existingBuilding.selectedValue.value : existingBuilding.valueInRentedCondition);
    
    //console.log('existingBuilding', existingBuilding);
    //console.log((_.isUndefined(existingBuilding.estimatedValueByMunicipality) || existingBuilding.estimatedValueByMunicipality === null || _.isUndefined(existingBuilding.surface)) && existingBuilding.address !== undefined);
    if ((_.isUndefined(existingBuilding.estimatedValueByMunicipality) || existingBuilding.estimatedValueByMunicipality === null || _.isUndefined(existingBuilding.surface)) && (existingBuilding.address !== undefined || this.model.address !== undefined)) {

      // Bugfix: pick the addres to retrieve info from, this kan be supplied through model.existingbuilding.address or model.address
      const address = existingBuilding.address ?? this.model.address;

      // Get building value if existing building doesn't have a value
      this.getAddressInfo(address, this.form, false);

    } else {
      this.form.get('estimatedValueByMunicipality').setValue(existingBuilding.estimatedValueByMunicipality, { emitEvent: false });
      this.form.get('estimatedValueByMunicipalityDate').setValue(existingBuilding.estimatedValueByMunicipalityDate, { emitEvent: false });
    }

    if (!_.isUndefined(existingBuilding.pointsRent?.totalPoints)) {
      this.form.get('pointsRent').get('totalPoints').setValue(existingBuilding.pointsRent.totalPoints);
    } else if (this.options.showRentalPoints === true) {
      this.updatePointsRent(this.form);
    }

    if (this.openRenovations().length > 0) {
      this.form.get('renovationCosts').setValue(this.openRenovationCosts(), { emitEvent: false });
      this.form.get('energyLabelAfterRenovation').setValue(this.bestEnergyLabelAfterRenovation(), { emitEvent: false });
      this.form.get('valueInRentedConditionAfterRenovation').setValue(this.highestValueInRentedConditionAfterRenovation(), { emitEvent: false });
      this.form.get('willBeRenovated').setValue(true, { emitEvent: false });
    } else {
      this.form.get('willBeRenovated').setValue(false, { emitEvent: false });
    }

    const recentValuations = _.orderBy((existingBuilding.valuations ?? []).filter(x => moment(x.valuation?.date).isAfter(moment().add(-1, 'year')) && x.valueInRentedCondition), x => x.valuation?.date, 'desc');

    if (recentValuations.length > 0) {
      const recentValuation = recentValuations[0];
      this.recentValuation = recentValuation;
      this.form.get('valueInRentedCondition').setValue(recentValuation.valueInRentedCondition);
    }

    this.buildingFormGroupChecker(this.form);
  }

  private doSaveBuilding(dirty: boolean): Observable<void> {
    return new Observable<void>(obs => {
      this.form.markAllAsTouched();

      if (this.isNewBuilding()) {
        this.buildingsService.addBuildings({
          body: [{
            address: this.model.address,           
            currentRentIncome: this.showCurrentRentIncome(this.form) ? parseInt(this.form.get('currentRentIncome').value, 10) : this.model.building?.currentRentIncome,
            customerId: this.model.customerId,
            divideValueInRentedConditionOverUnits: this.form.get('divideValueInRentedConditionOverUnits').value,
            energyIndex: this.showEnergy(this.form) ? this.form.get('energyIndex').value : this.model.building?.energyIndex,
            energyPerformanceExpiryDate: this.showEnergy(this.form) ? this.form.get('energyPerformanceExpiryDate').value : this.model.building?.energyPerformanceExpiryDate,
            energyPerformanceRegistrationDate: this.showEnergy(this.form) ? this.form.get('energyPerformanceRegistrationDate').value : this.model.building?.energyPerformanceRegistrationDate,
            estimatedRentalValue: this.showEstimatedRentalValue(this.form) ? parseInt(this.form.get('estimatedRentalValue').value, 10) : this.model.building?.estimatedRentalValue,
            estimatedValueByMunicipality: this.showEstimatedValueByMunicipality(this.form) ? this.form.get('estimatedValueByMunicipality').value : this.model.building?.estimatedValueByMunicipality,
            estimatedValueByMunicipalityDate: this.showEstimatedValueByMunicipality(this.form) ? this.form.get('estimatedValueByMunicipalityDate').value : this.model.building?.estimatedValueByMunicipalityDate,
            expectedValue: this.showExpectedValue(this.form) ? this.form.get('expectedValue').value : null,
            finalEnergyLabel: this.showEnergy(this.form) ? this.form.get('finalEnergyLabel').value : this.model.building?.finalEnergyLabel,
            groundLease: this.showGroundLease() ? this.form.get('groundLease').value : this.model.building?.groundLease,
            groundLeaseEndDate: this.model.building?.groundLeaseEndDate,
            numberOfRentalContracts: this.form.get('numberOfRentalContracts').value,
            numberOfSubletContracts: this.numberOfSubletContracts(this.form),
            pointsRent: this.showRentalPoints(this.form) ? { manualPoints: this.form.get('pointsRent').get('manualPoints').value } : null,
            purchaseDate: this.showPurchaseDate() ? this.form.get('purchaseDate').value : this.model.building?.purchaseDate,
            purchaseValue: this.showPurchaseValue() ? this.form.get('purchaseValue').value : this.model.building?.purchaseValue,
            purpose: this.form.get('purpose').value,
            rentalType: this.form.get('rentalType').value,
            renovations: this.willBeRenovated(this.form) ? [{
              costs: this.form.get('renovationCosts').value,
              energyLabelAfterRenovation: this.form.get('energyLabelAfterRenovation').value,
              valueInRentedConditionAfterRenovation: this.form.get('valueInRentedConditionAfterRenovation').value,
              permitsRequired: this.form.get('renovationPermitsRequired').value
            }] : [],
            rented: this.form.get('rented').value,
            rentedTo: this.form.get('rentedTo').value,
            rooms: this.isRoomRental(this.form) ? this.createRooms() : [],
            status: this.form.get('status').value,
            sublet: this.form.get('sublet').value ?? false,
            surface: this.showSurface(this.form) ? this.form.get('surface').value : this.model.building.surface,
            type: this.form.get('type').value,
            units: this.createUnits(),
            valueInRentedCondition: this.form.get('valueInRentedCondition').enabled ? this.form.get('valueInRentedCondition').value : undefined,
            valueInRentedConditionCommercial: this.form.get('valueInRentedConditionCommercial').enabled ? this.form.get('valueInRentedConditionCommercial').value : undefined,
            valueInRentedConditionResidential: this.form.get('valueInRentedConditionResidential').enabled ? this.form.get('valueInRentedConditionResidential').value : undefined
          } as NewBuilding]
        })
          .subscribe(buildings => {
            this.loadExistingBuilding(buildings[0]);

            this.buildingsService
              .getRenovations({ id: buildings[0].id })
              .subscribe(renovations => {
                this.model.building.renovations = renovations;

                const processes: Observable<boolean>[] = [];
                processes.push(this.addBuildingValue());

                if (this.options.linkToMortgage === true) {
                  processes.push(this.linkMortgages());
                }

                from(processes)
                  .pipe(concatMap(x => x))
                  .pipe(last())
                  .subscribe((b) => {
                    this.markAsProcessed(dirty);
                    obs.next();
                    obs.complete();
                  });
              });
          });
      } else {
        // Existing building -> patch it!
        const processes: Observable<boolean>[] = [];

        processes.push(this.processRenovations());
        processes.push(this.processUnits());
        processes.push(this.processRooms());
        processes.push(this.doPatchBuilding());
        processes.push(this.addBuildingValue());

        if (this.options.linkToMortgage === true) {
          processes.push(this.linkMortgages());
        }

        from(processes)
          .pipe(concatMap(x => x))
          .pipe(last())
          .subscribe((b) => {
            this.markAsProcessed(dirty);
            obs.next();
            obs.complete();
          });
      }
    });
  }

  private isNewBuilding(): boolean {
    return !this.model.building || !this.model.building.id || this.model.building.id === EMPTY_GUID;
  }

  private createUnits(): NewUnit[] {
    return this.units.map(x => {
      const unit = {
        address: x.address,
        currentRentIncome: this.showCurrentRentIncome(x.form) ? parseInt(x.form.get('currentRentIncome').value, 10) : x.unit?.currentRentIncome,       
        energyIndex: this.showEnergy(x.form) ? x.form.get('energyIndex').value : x.unit?.energyIndex, 
        energyPerformanceExpiryDate: this.showEnergy(x.form) ? x.form.get('energyPerformanceExpiryDate').value : x.unit?.energyPerformanceExpiryDate,
        energyPerformanceRegistrationDate: this.showEnergy(x.form) ? x.form.get('energyPerformanceRegistrationDate').value : x.unit?.energyPerformanceRegistrationDate,
        estimatedRentalValue: this.showEstimatedRentalValue(x.form) ? parseInt(x.form.get('estimatedRentalValue').value, 10) : x.unit?.estimatedRentalValue,
        estimatedValueByMunicipality: this.showEstimatedValueByMunicipality(x.form) ? x.form.get('estimatedValueByMunicipality').value : x.unit?.estimatedValueByMunicipality,
        estimatedValueByMunicipalityDate: this.showEstimatedValueByMunicipality(x.form) ? x.form.get('estimatedValueByMunicipalityDate').value : x.unit?.estimatedValueByMunicipalityDate,
        finalEnergyLabel: this.showEnergy(x.form) ? x.form.get('finalEnergyLabel').value : x.unit?.finalEnergyLabel,
        numberOfRentalContracts: x.form.get('numberOfRentalContracts').value,
        numberOfSubletContracts: this.numberOfSubletContracts(x.form),
        pointsRent: this.showRentalPoints(x.form) ? { manualPoints: x.form.get('pointsRent').get('manualPoints').value } : null,
        purpose: this.form.get('purpose').value,
        rentalType: this.form.get('rentalType').value,
        rented: x.form.get('rented').value,
        rentedTo: this.form.get('rentedTo').value,
        status: this.form.get('status').value,
        sublet: x.form.get('sublet').value ?? false,
        surface: this.showSurface(x.form) ? x.form.get('surface').value : x.unit.surface,
        type: x.form.get('type').value
      } as NewUnit;

      return unit;
    });

  }

  private createRooms(): NewRoom[] {
    const rooms: NewRoom[] = [];
    for (let index = 0; index < this.numberOfRentalContracts(this.form); index++) {
      const room = {} as NewRoom;
      room.name = `${this.translateService.instant('Room')}  ${index + 1}`;
      room.currentRentIncome = this.isRented(this.form) ? Math.round(this.form.get('currentRentIncome').value / this.numberOfRentalContracts(this.form)) : null;
      room.estimatedRentalValue = !this.isRented(this.form) ? Math.round(this.form.get('estimatedRentalValue').value / this.numberOfRentalContracts(this.form)) : null;
      room.pointsRent = Math.round(this.getRentFilled(this.form) / this.numberOfRentalContracts(this.form));
      room.surface = Math.round(this.form.get('surface').value / this.numberOfRentalContracts(this.form));
      room.rented = this.form.get('rented').value;

      rooms.push(room);
    }
    return rooms;
  }

  private processRenovations(): Observable<boolean> {

    // First determine what should happen to the existing renovations
    // since the backend supports multiple, this frontend just supports 1

    return new Observable(obs => {
      // Flow 0: Ignore renovations
      if (this.options.showRenovations === false) {
        obs.next(false);
        obs.complete();

        // Flow 1: Multiple renovations conflicting with new renovation settings
      } else if (this.openRenovationsConflictWithCurrentRenovation()) {

        console.log('Flow 1', this.openRenovations());
        const deleteRenovationRequests = this.openRenovations().map(r => this.buildingsService.deleteRenovationById({ id: this.model.building.id, renovationId: r.id }));

        from(deleteRenovationRequests).pipe(
          concatMap(x => x)
        ).pipe(last()).subscribe((b) => {

          // Removed all renovations, but a new one might be created
          this.addNewRenovation().subscribe(x => {
            obs.next(true);
            obs.complete();
          });

        });

        // Flow 2: Single renovation
      } else if (this.openRenovations().length === 1 && this.willBeRenovated(this.form)) {

        // Flow 2.1: Properties have changed => Patch the renovation
        if (this.openRenovations()[0].costs !== +this.form.get('renovationCosts').value ||
          this.openRenovations()[0].valueInRentedConditionAfterRenovation !== +this.form.get('valueInRentedConditionAfterRenovation').value ||
          this.openRenovations()[0].energyLabelAfterRenovation !== this.form.get('energyLabelAfterRenovation').value) {
          console.log('Flow 2.1');

          // Patch the existing renovation
          this.buildingsService.patchRenovation({
            id: this.model.building.id,
            renovationId: this.openRenovations()[0].id,
            body: [
              { op: 'replace', path: 'costs', value: this.form.get('renovationCosts').value },
              { op: 'replace', path: 'energyLabelAfterRenovation', value: this.form.get('energyLabelAfterRenovation').value },
              { op: 'replace', path: 'permitsRequired', value: this.form.get('renovationPermitsRequired').value },
              { op: 'replace', path: 'valueInRentedConditionAfterRenovation', value: this.form.get('valueInRentedConditionAfterRenovation').value }
            ]
          }).subscribe(renovation => {
            obs.next(true);
            obs.complete();
          });
        } else {
          console.log('Flow 2.2');
          // Flow 2.2: Properties have not changed => Ignore the renovation
          obs.next(false);
          obs.complete();
        }

        // Flow 3: No conflicting settings => a new renovation may be created
      } else {
        this.addNewRenovation().subscribe(x => {
          obs.next(true);
          obs.complete();
        });
      }
    });
  }

  /**
   * Process the updated building to check for certain warnings
   * to be triggered
   *
   *
   */
  private canProcessThisBuilding(): boolean {

    /* Only process this limit once */
    if (this.limitedByPoints() && this.limitedByPointsRent === false) {
      this.limitedByPointsRent = true;
      this.model.isProcessed = false;

      return false;
    }

    return true;
  }

  limitedByPoints(): boolean {
    return (this.options.skipPointsRentWarning === false &&
      (!this.isForRent(this.form) || this.isRentFilled(this.form)) && // Only if rental values are required, because if left empty the user implicitly excepts system values
      this.isPointsRent(this.form) &&
      this.model.building.pointsRent.freeSector === false &&
      !this.model.building.pointsRent.manualPoints && 
      this.model.building.pointsRent.rent < this.getRentFilled(this.form));
  }

  /**
   * Checks if a new renovation should be added, and patches the building afterwards,
   * or starts patching the building immediately
   */
  private addNewRenovation(): Observable<boolean> {

    return new Observable(obs => {
      if (this.willBeRenovated(this.form)) {
        this.buildingsService.addRenovationById({
          id: this.model.building.id,
          body: {
            costs: this.form.get('renovationCosts').value,
            energyLabelAfterRenovation:this.form.get('energyLabelAfterRenovation').value,
            valueInRentedConditionAfterRenovation: this.form.get('valueInRentedConditionAfterRenovation').value,
            description: this.translateService.instant('Renovation'),
            permitsRequired: this.form.get('renovationPermitsRequired').value
          }
        }).subscribe(x => {
          obs.next(true);
          obs.complete();
        });
      } else {
        obs.next(false);
        obs.complete();
      }
    });
  }


  private doPatchBuilding(): Observable<boolean> {

    return new Observable(obs => {

      const buildingOperations = [
        { op: 'replace', path: 'status', value: this.form.get('status').value },
        { op: 'replace', path: 'type', value: this.form.get('type').value },
        { op: 'replace', path: 'purpose', value: this.form.get('purpose').value }
      ];

      if (this.form.get('rentalType').enabled) {
        buildingOperations.push({ op: 'replace', path: 'rentalType', value: this.form.get('rentalType').value });
      }

      if (this.form.get('rented').enabled) {
        buildingOperations.push({ op: 'replace', path: 'rented', value: this.form.get('rented').value });
      }

      if (this.form.get('rentedTo').enabled) {
        buildingOperations.push({ op: 'replace', path: 'rentedTo', value: this.form.get('rentedTo').value });
      }

      if (this.form.get('currentRentIncome').enabled) {
        buildingOperations.push({ op: 'replace', path: 'currentRentIncome', value: this.form.get('currentRentIncome').value });
      }

      if (this.form.get('estimatedRentalValue').enabled) {
        buildingOperations.push({ op: 'replace', path: 'estimatedRentalValue', value: this.form.get('estimatedRentalValue').value });
      }

      if (this.form.get('numberOfRentalContracts').enabled) {
        buildingOperations.push({ op: 'replace', path: 'numberOfRentalContracts', value: this.form.get('numberOfRentalContracts').value });
      }

      if (this.form.get('numberOfSubletContracts').enabled) {
        buildingOperations.push({ op: 'replace', path: 'numberOfSubletContracts', value: this.form.get('numberOfSubletContracts').value });
      }

      if (this.form.get('sublet').enabled) {
        buildingOperations.push({ op: 'replace', path: 'sublet', value: this.form.get('sublet').value });
      }

      /* Skip some operations for combination buildings */
      // if (!this.isCombinationBuilding(this.form)) {
      //   buildingOperations.push({ op: 'replace', path: 'calculateNetInitialYield', value: false });
      // }

      if (this.form.get('purchaseDate').enabled) {
        buildingOperations.push({ op: 'replace', path: 'purchaseDate', value: this.form.get('purchaseDate').value });
      }

      if (this.form.get('purchaseValue').enabled) {
        buildingOperations.push({ op: 'replace', path: 'purchaseValue', value: this.form.get('purchaseValue').value });
      }

      if (this.form.get('valueInRentedConditionResidential').enabled) {
        buildingOperations.push({ op: 'replace', path: 'valueInRentedConditionResidential', value: this.form.get('valueInRentedConditionResidential').value });
      }

      if (this.form.get('valueInRentedConditionCommercial').enabled) {
        buildingOperations.push({ op: 'replace', path: 'valueInRentedConditionCommercial', value: this.form.get('valueInRentedConditionCommercial').value });
      }

      if (this.form.get('valueInRentedCondition').enabled) {
        buildingOperations.push({ op: 'replace', path: 'valueInRentedCondition', value: this.form.get('valueInRentedCondition').value });
      }

      if (this.form.get('expectedValue').enabled) {
        buildingOperations.push({ op: 'replace', path: 'expectedValue', value: this.form.get('expectedValue').value });
      }

      if (this.form.get('pointsRent').get('manualPoints').enabled) {
        buildingOperations.push({ op: 'replace', path: 'pointsRent/manualPoints', value: this.form.get('pointsRent').get('manualPoints').value });
      }

      if (this.form.get('surface').enabled) {
        buildingOperations.push({ op: 'replace', path: 'surface', value: this.form.get('surface').value });
      }

      if (this.form.get('finalEnergyLabel').enabled) {
        buildingOperations.push({ op: 'replace', path: 'finalEnergyLabel', value: this.form.get('finalEnergyLabel').value });
      }

      if (this.form.get('groundLease').enabled) {
        buildingOperations.push({ op: 'replace', path: 'groundLease', value: this.form.get('groundLease').value });
      }

      if (this.form.get('estimatedValueByMunicipality').enabled) {
        buildingOperations.push({ op: 'replace', path: 'estimatedValueByMunicipality', value: this.form.get('estimatedValueByMunicipality').value });
      }

      if (this.form.get('estimatedValueByMunicipalityDate').enabled) {
        buildingOperations.push({ op: 'replace', path: 'estimatedValueByMunicipalityDate', value: this.form.get('estimatedValueByMunicipalityDate').value });
      }

      this.buildingsService.patchBuilding({
        id: this.model.building.id,
        body: buildingOperations
      })
        .subscribe(updatedBuilding => {

          // Refresh the building, including renovations
          this.model.building = updatedBuilding;

          this.buildingsService
            .getRenovations({ id: updatedBuilding.id })
            .subscribe(renovations => {
              this.model.building.renovations = renovations;

              obs.next(true);
              obs.complete();

            });

        });

    });
  }


  private processUnits(): Observable<boolean> {

    return new Observable(obs => {
      if (this.units.length === 0) {
        obs.next(true);
        obs.complete();
      } else {

        /* Build a list of unit patches => Value In Rented Condition is patched on the combination building */
        const patchUnitRequests = this.units.map(unitRow => {
          const unitOperations = [
            { op: 'replace', path: 'type', value: unitRow.form.get('type').value },
            //{ op: 'replace', path: 'calculateNetInitialYield', value: false }
          ];

         
          if (unitRow.form.get('rentalType').enabled) {
            unitOperations.push({ op: 'replace', path: 'rentalType', value: unitRow.form.get('rentalType').value });
          }

          if (unitRow.form.get('rented').enabled) {
            unitOperations.push({ op: 'replace', path: 'rented', value: unitRow.form.get('rented').value });
          }

          if (unitRow.form.get('rentedTo').enabled) {
            unitOperations.push({ op: 'replace', path: 'rentedTo', value: unitRow.form.get('rentedTo').value });
          }
    
          if (unitRow.form.get('currentRentIncome').enabled) {
            unitOperations.push({ op: 'replace', path: 'currentRentIncome', value: unitRow.form.get('currentRentIncome').value });
          }
    
          if (unitRow.form.get('estimatedRentalValue').enabled) {
            unitOperations.push({ op: 'replace', path: 'estimatedRentalValue', value: unitRow.form.get('estimatedRentalValue').value });
          }
        
          if (unitRow.form.get('numberOfRentalContracts').enabled) {
            unitOperations.push({ op: 'replace', path: 'numberOfRentalContracts', value: unitRow.form.get('numberOfRentalContracts').value });
          }
    
          if (unitRow.form.get('numberOfSubletContracts').enabled) {
            unitOperations.push({ op: 'replace', path: 'numberOfSubletContracts', value: unitRow.form.get('numberOfSubletContracts').value });
          }
        
          if (unitRow.form.get('sublet').enabled) {
            unitOperations.push({ op: 'replace', path: 'sublet', value: unitRow.form.get('sublet').value });
          }
    
          if (unitRow.form.get('finalEnergyLabel').enabled) {
            unitOperations.push({ op: 'replace', path: 'finalEnergyLabel', value: unitRow.form.get('finalEnergyLabel').value });
          }

          if (unitRow.form.get('pointsRent').get('manualPoints').enabled) {
            unitOperations.push({ op: 'replace', path: 'pointsRent/manualPoints', value: unitRow.form.get('pointsRent').get('manualPoints').value });
          }

          if (unitRow.form.get('surface').enabled) {
            unitOperations.push({ op: 'replace', path: 'surface', value: unitRow.form.get('surface').value });
          }

          if (unitRow.form.get('estimatedValueByMunicipality').enabled) {
            unitOperations.push({ op: 'replace', path: 'estimatedValueByMunicipality', value: unitRow.form.get('estimatedValueByMunicipality').value });
          }

          if (unitRow.form.get('estimatedValueByMunicipalityDate').enabled) {
            unitOperations.push({ op: 'replace', path: 'estimatedValueByMunicipalityDate', value: unitRow.form.get('estimatedValueByMunicipalityDate').value });
          }

          return this.buildingsService.patchBuilding({ id: unitRow.unit.id, body: unitOperations });

        });

        from(patchUnitRequests).pipe(
          concatMap(x => x)
        )
          .pipe(last()).subscribe((b) => {
            obs.next(true);
            obs.complete();
          });

      }
    });
  }

  private processRooms(): Observable<boolean> {
    /* Detect changes in room rental */
    return new Observable(obs => {

      const requests: Observable<any>[] = [];

      /* Room rental is switched on, create rooms */
      if (this.isRoomRental(this.form) && this.model.building.rentalType != 'RoomRental') {
        requests.push(... this.createRooms().map(r => this.buildingsService.addRoomById({ id: this.model.building.id, body: r })));
      } 
      else if (!this.isRoomRental(this.form) && this.model.building.rentalType == 'RoomRental') {
        /* Room rental is switched off, remove rooms */
        requests.push(...this.rooms.map(r => this.buildingsService.deleteUnitById({ unitId: r.id, id: this.model.building.id })));
      }
      else if (this.isRoomRental(this.form)) {
        /* Room rental details have changed, remove rooms and create rooms */
        requests.push(...this.rooms.map(r => this.buildingsService.deleteUnitById({ unitId: r.id, id: this.model.building.id })));
        requests.push(... this.createRooms().map(r => this.buildingsService.addRoomById({ id: this.model.building.id, body: r })));
      } 

      if (requests.length > 0) {
        from(requests)
          .pipe(concatMap(x => x))
          .pipe(last())
          .subscribe((b) => {
            obs.next(true);
            obs.complete();
          });
      } else {
        obs.next(false);
        obs.complete();
      }

    });

  }

  /**
   * An extra patch on rental values enforces the desired rent values
   * on the building, which can change inflight because overruling the system's
   * rental points
   */
  private addBuildingValue(): Observable<boolean> {
    return new Observable(obs => {
      // Not filled in, just continue without setting a value
      if ((!this.isValueInRentedConditionFilled(this.form))) {
        obs.next(false);
        obs.complete();
      } else {
        this.model.isProcessing = true;

        const valueInRentedCondition = this.willBeRenovated(this.form) ? this.form.get('valueInRentedConditionAfterRenovation').value : this.form.get('valueInRentedCondition').value;

        this.buildingsService
          .putSelectedBuildingValue({
            id: this.model.building.id, body: {
              source: this.willBeRenovated(this.form) ? 'Renovation' : 'Organization',
              value: parseInt(valueInRentedCondition, 10)
            }
          })
          .subscribe((selectedValue) => {
            this.model.building.valueInRentedConditionClient = valueInRentedCondition;
            obs.next(true);
            obs.complete();

          });
      }
    });
  }

  private linkMortgages(): Observable<boolean> {
    return new Observable<boolean>(obs => {
      const addMortgageBuildings = this.model.mortgageIds.map(mortgageId =>
        this.mortgagesService.getMortgageBuildings({ id: mortgageId }).pipe(
          map(buildings => ({ mortgageId: mortgageId, buildings })),
          concatMap(result1 =>
            of(result1).pipe(
              concatMap(mortgageBuildings => {
                if (mortgageBuildings.buildings.find(building => building.buildingId === this.model.building.id) === undefined) {
                  console.info(`Add building '${this.model.building.id}' to mortgage '${mortgageBuildings.mortgageId}'`);
                  return this.mortgagesService.addMortgageBuildings({ id: mortgageBuildings.mortgageId, body: [this.model.building] });
                } else {
                  console.info(`Building '${this.model.building.id}' already exists on mortgage '${mortgageBuildings.mortgageId}'`);
                  return of(null);
                }
              })
            )
          )
        )
      );

      if (addMortgageBuildings.length === 0) {
        obs.next(true);
        obs.complete();
      } else {
        concat(...addMortgageBuildings)
          .pipe(last())
          .subscribe(() => {
            obs.next(true);
            obs.complete();
          });
      }
    });
  }

  private markAsProcessed(dirty: boolean): void {
    this.confirmedDeletingOpenRenovations = false;
    this.model.isProcessing = false;
    this.loadModel();
    if (dirty === false && this.canProcessThisBuilding()) {
      this.model.isProcessed = true;

      console.log(`http://localhost:4200/customers/${this.model.building.customerId}/buildings/${this.model.building.id}/detail`);
      this.buildingProcessed.emit(this.model);
    }
  }

  /**
    * Returns if the open renovations on this building
    * conflict with the current
    */
  private openRenovationsConflictWithCurrentRenovation(): boolean {

    // Building has renovations, but user indicates it's not being renovated
    if (this.openRenovations().length > 0 && !this.willBeRenovated(this.form)) {
      return true;
    }

    // There are multiple open renovations, but the total of renovation costs are being changed
    if (this.openRenovations().length > 1 && this.willBeRenovated(this.form) && this.openRenovationCosts() !== +this.form.get('renovationCosts').value) {
      return true;
    }

    // There are multiple open renovations, but the highest value in rented condition is being changed
    if (this.openRenovations().length > 0 && this.willBeRenovated(this.form) && this.highestValueInRentedConditionAfterRenovation() !== +this.form.get('valueInRentedConditionAfterRenovation').value) {
      return true;
    }

    return false;
  }

  /**
   * Shows the popup about existing renovations being impacted by the new settings
   */
  private showDeletedRenovationWarning(): void {

    if (this.options.showRenovations && !this.confirmedDeletingOpenRenovations && this.openRenovationsConflictWithCurrentRenovation()) {

      // Alert user renovations might be removed
      const data = {
        title: this.translateService.instant('Remove earlier renovations?'),
        message: this.translateService.instant('Changing the existing renovation details will delete previous renovations'),
        list: this.openRenovations().map(r => `${r.description ? `${r.description} - ` : ''}€ ${r.costs.toFixed(0)} (${this.translateService.instant('entered on')} ${moment(r.modified).format('DD-MM-yyyy')} ${this.translateService.instant('by')} ${r.modifiedBy})`)
      } as ConfirmDialogData;

      Shell
        .confirmationDialog(this.matDialog, ConfirmDialogComponent, data)
        .subscribe((result: boolean) => {

          if (!result) {
            // The user cancels, back to reality
            const hasOpenRenovations = this.openRenovations().length > 0;
            this.form.get('willBeRenovated').setValue(hasOpenRenovations, { emitEvent: false });
            this.form.get('renovationCosts').setValue(this.openRenovationCosts(), { emitEvent: false });
            this.form.get('valueInRentedConditionAfterRenovation').setValue(this.highestValueInRentedConditionAfterRenovation(), { emitEvent: false });

          } else {
            // The user confirms, let's never warn again
            this.confirmedDeletingOpenRenovations = true;
          }
        });
    }
  }
}
